import { withLayouts } from 'react-dx'
import { Trans, useLingui } from '@lingui/react/macro'

import RootLayout from '../_layout/layouts/RootLayout'
import DefaultLayout from '../_layout'
import AntdLayout from '../_layout/layouts/AntdLayout'
import { COMPANY_NAME } from '../_constant/biz'

import { EmailLink, SiteLink } from './_components/ArticleLink'

import SiteSpecificContextLayout from '@/pages/tad.ai/_layout/layouts/SiteSpecificContextLayout'
import { getServerSideData } from '@/helpers/server-data'
import { useIsAppQuery } from '@/hooks/useIsAppQuery'

const Page = () => {
  const { t } = useLingui()
  return (
    <div
      className='
      mx-auto max-w-[1092px] px-4
      pb-12 lg:pb-24 
      [&_h2]:mb-3 [&_h2]:mt-8 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-white [&_h2]:md:text-2xl 
      [&_h3]:my-3 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-white [&_h3]:md:text-lg 
      [&_h4]:my-3 [&_h4]:text-sm [&_h4]:font-semibold [&_h4]:text-white [&_h4]:md:text-base 
      [&_li]:mb-2 [&_li]:md:text-sm 
      [&_ol]:list-decimal [&_ol]:ps-5 [&_ol]:text-sm [&_ol]:text-white [&_ol]:opacity-85
      [&_p]:mb-2 [&_p]:text-sm [&_p]:text-white [&_p]:opacity-85 [&_p]:md:text-sm 
      [&_ul]:list-disc [&_ul]:ps-5 [&_ul]:text-sm [&_ul]:text-white [&_ul]:opacity-85
      '
    >
      <h1 className='text-f-primary mb-8 mt-12 text-center text-[24px] font-bold leading-[36px] md:mt-20 md:text-[56px] md:leading-[72px]'>{t`Privacy Policy`}</h1>
      <div className='rounded-3xl bg-black shadow-md lg:bg-[#171717] lg:p-16 lg:px-20'>
        <p>
          <Trans>Last updated: March 16, 2026</Trans>
        </p>
        <p>
          <Trans>
            This Privacy Policy describes Our policies and procedures on the
            collection, use and disclosure of Your information when You use the
            Service and tells You about Your privacy rights and how the law
            protects You.
          </Trans>
        </p>
        <p>
          <Trans>
            Tad AI uses Your Personal data to provide and improve the Service.
            By using the Service, You agree to the collection and use of
            information in accordance with this Privacy Policy.
          </Trans>
        </p>

        <h2 className='font-bold leading-[36px] md:mt-20 md:text-[56px] md:leading-[72px]'>
          <Trans>Interpretation and Definitions</Trans>
        </h2>
        <h3>
          <Trans>Interpretation</Trans>
        </h3>
        <p>
          <Trans>
            The words of which the initial letter is capitalized have meanings
            defined under the following conditions. The following definitions
            shall have the same meaning regardless of whether they appear in
            singular or in plural.
          </Trans>
        </p>
        <h3>
          <Trans>Definitions</Trans>
        </h3>
        <p>
          <Trans>For the purposes of this Privacy Policy:</Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>Account</span> means the unique account
            created by You to access our Service or parts of our Service.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>Company</span> (referred to as either
            "the Company", "We", "Us" or "Our" in this Agreement) refers to{' '}
            {COMPANY_NAME}, 15 SCOTTS ROAD #03-12 15 SCOTTS SINGAPORE 228218.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>Cookies</span> are small files that are
            placed on Your computer, mobile device or any other device by a
            website, containing the details of Your browsing history on that
            website among its many uses.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>Device</span> means any device that can
            access the Service such as a computer, a cellphone or a digital
            tablet.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>Personal Data</span> is any information
            that relates to an identified or identifiable individual. Service
            refers to the Website.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>Usage Data</span> refers to data
            collected automatically, either generated by the use of the Service
            or from the Service infrastructure itself (for example, the duration
            of a page visit).
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>Website</span> refers to Tad AI,
            accessible from <SiteLink text='https://tad.ai' />
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>You</span> means the individual
            accessing or using the Service, or the company, or other legal
            entity on behalf of which such individual is accessing or using the
            Service, as applicable.
          </Trans>
        </p>
        <h2 className='font-bold leading-[36px] md:mt-20 md:text-[56px] md:leading-[72px]'>
          <Trans>Third-Party AI Services and Data Sharing</Trans>
        </h2>
        <p>
          <Trans>
            To provide our advanced AI music generation features, we integrate
            with specific third-party Artificial Intelligence (AI) service
            providers. We only share the minimum necessary data required to
            generate your requested content.
          </Trans>
        </p>
        <h3>
          <Trans>Data Shared and Providers</Trans>
        </h3>
        <p>
          <Trans>
            When you use our AI features, the following data is transmitted to
            our designated third-party AI providers:
          </Trans>
        </p>
        <ul>
          <li>
            <Trans>
              User Inputs: Text prompts, commands, style descriptions, uploaded
              audio, and uploaded images.
            </Trans>
          </li>
        </ul>
        <h3>
          <Trans>How Third Parties Use Your Data</Trans>
        </h3>
        <p>
          <Trans>
            We strictly limit the use of your data by third-party providers to
            the sole purpose of providing the requested AI generation service.
            The specific providers and their data usage are as follows:
          </Trans>
        </p>
        <ul>
          <li>
            <Trans>
              <span className='font-bold'>Deepseek (DeepSeek-V3.1): </span>
              Receives your text commands and prompts when you submit a request,
              solely to generate lyrics.
            </Trans>
          </li>
          <li>
            <Trans>
              <span className='font-bold'>OpenAI (GPT-4o mini): </span>
              Receives your uploaded images when you submit a request, utilizing
              image recognition and analysis solely to generate lyrics.
            </Trans>
          </li>
          <li>
            <Trans>
              <span className='font-bold'>OpenAI (GPT-4.1 mini): </span>
              Receives your text commands and generated lyrics when you submit a
              request, solely to generate style content and descriptions.
            </Trans>
          </li>
          <li>
            <Trans>
              <span className='font-bold'>Suno, Inc. (Suno V5): </span>
              Receives your text commands, generated lyrics, and style
              descriptions when you submit a request, solely to generate the
              final music files.
            </Trans>
          </li>
          <li>
            <Trans>
              <span className='font-bold'>Kunlun (Mureka): </span>
              Receives your text commands, generated lyrics, and style
              descriptions when you submit a request, solely to generate the
              final music files.
            </Trans>
          </li>
        </ul>
        <h3>
          <Trans>Data Protection and Anonymization</Trans>
        </h3>
        <p>
          <Trans>
            We confirm that all third-party AI service providers mentioned above
            are contractually obligated to provide the same or equal protection
            of your user data as stated in this Privacy Policy and as required
            by the Apple App Store Review Guidelines.
          </Trans>
        </p>
        <p>
          <Trans>
            We practice data minimization and do not share sensitive personally
            identifiable information (such as your account ID, email address,
            real name, or precise geolocation) with these AI providers. Where
            technically feasible, all data transmitted for AI processing is
            anonymized.
          </Trans>
        </p>
        <h2 className='font-bold leading-[36px] md:mt-20 md:text-[56px] md:leading-[72px]'>
          <Trans>Collecting and Using Your Personal Data</Trans>
        </h2>
        <h3>
          <Trans>Types of Data Collected</Trans>
        </h3>
        <h4>
          <Trans>Personal Data</Trans>
        </h4>
        <p>
          <Trans>
            While using Our Service, We may ask You to provide Us with certain
            personally identifiable information that can be used to contact or
            identify You. Personally identifiable information may include, but
            is not limited to:
          </Trans>
        </p>
        <ul>
          <li>
            <Trans>Email address</Trans>
          </li>
          <li>
            <Trans>First name and last name</Trans>
          </li>
          <li>
            <Trans>Address, State, Province, ZIP/Postal code, City</Trans>
          </li>
          <li>
            <Trans>Usage Data</Trans>
          </li>
        </ul>
        <h4>
          <Trans>Usage Data</Trans>
        </h4>
        <p>
          <Trans>
            Usage Data is collected automatically when using the Service.
          </Trans>
        </p>
        <p>
          <Trans>
            Usage Data may include information such as Your Device's Internet
            Protocol address (e.g. IP address), browser type, browser version,
            the pages of our Service that You visit, the time and date of Your
            visit, the time spent on those pages, unique device identifiers and
            other diagnostic data.
          </Trans>
        </p>
        <p>
          <Trans>
            When You access the Service by or through a mobile device, We may
            collect certain information automatically, including, but not
            limited to, the type of mobile device You use, Your mobile device
            unique ID, the IP address of Your mobile device, Your mobile
            operating system, the type of mobile Internet browser You use,
            unique device identifiers and other diagnostic data.
          </Trans>
        </p>
        <p>
          <Trans>
            We may also collect information that Your browser sends whenever You
            visit our Service or when You access the Service by or through a
            mobile device.
          </Trans>
        </p>

        <h4>
          <Trans>Tracking Technologies and Cookies</Trans>
        </h4>
        <p>
          <Trans>
            We use Cookies and similar tracking technologies to track the
            activity on Our Service and store certain information. Tracking
            technologies used are beacons, tags, and scripts to collect and
            track information and to improve and analyze Our Service. The
            technologies We use may include:
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>Cookies or Browser Cookies.</span> A
            cookie is a small file placed on Your Device. You can instruct Your
            browser to refuse all Cookies or to indicate when a Cookie is being
            sent. However, if You do not accept Cookies, You may not be able to
            use some parts of our Service. Unless you have adjusted Your browser
            setting so that it will refuse Cookies, our Service may use Cookies.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>Web Beacons.</span> Certain sections of
            our Service and our emails may contain small electronic files known
            as web beacons (also referred to as clear gifs, pixel tags, and
            single-pixel gifs) that permit the Company, for example, to count
            users who have visited those pages or opened an email and for other
            related website statistics (for example, recording the popularity of
            a certain section and verifying system and server integrity).
          </Trans>
        </p>
        <p>
          <Trans>
            Cookies can be "Persistent" or "Session" Cookies. Persistent Cookies
            remain on Your personal computer or mobile device when You go
            offline, while Session Cookies are deleted as soon as You close Your
            web browser.
          </Trans>
        </p>
        <p>
          <Trans>
            We use both Session and Persistent Cookies for the purposes set out
            below:
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>Necessary / Essential Cookies</span>
          </Trans>
        </p>
        <p>
          <Trans>Type: Session Cookies</Trans>
        </p>
        <p>
          <Trans>Administered by: Us</Trans>
        </p>
        <p>
          <Trans>
            Purpose: These Cookies are essential to provide You with services
            available through the Website and to enable You to use some of its
            features. They help to authenticate users and prevent fraudulent use
            of user accounts. Without these Cookies, the services that You have
            asked for cannot be provided, and We only use these Cookies to
            provide You with those services.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>
              Cookies Policy / Notice Acceptance Cookies
            </span>
          </Trans>
        </p>
        <p>
          <Trans>Type: Persistent Cookies</Trans>
        </p>
        <p>
          <Trans>Administered by: Us</Trans>
        </p>
        <p>
          <Trans>
            Purpose: These Cookies identify if users have accepted the use of
            cookies on the Website.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>Functionality Cookies</span>
          </Trans>
        </p>
        <p>
          <Trans>Type: Persistent Cookies</Trans>
        </p>
        <p>
          <Trans>Administered by: Us</Trans>
        </p>
        <p>
          <Trans>
            Purpose: These Cookies allow us to remember choices You make when
            You use the Website, such as remembering your login details or
            language preference. The purpose of these Cookies is to provide You
            with a more personal experience and to avoid You having to re-enter
            your preferences every time You use the Website.
          </Trans>
        </p>
        <h1>
          <Trans>Use of Your Personal Data</Trans>
        </h1>
        <p>
          <Trans>
            The Company may use Personal Data for the following purposes:
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>
              To provide and maintain our Service
            </span>
            , including to monitor the usage of our Service
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>To manage Your Account:</span> The
            Personal Data You provide can give You access to different
            functionalities of the Service that are available to You as a
            registered user.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>
              For the performance of a contract:
            </span>
            the development, compliance and undertaking of the purchase contract
            for the products, items or services You have purchased or of any
            other contract with Us through the Service.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>To contact You:</span>
            To contact You by email, telephone calls, SMS, or other equivalent
            forms of electronic communication, such as a mobile application's
            push notifications regarding updates or informative communications
            related to the functionalities, products or contracted services,
            including the security updates, when necessary or reasonable for
            their implementation.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>To provide You </span>
            with news, special offers and general information about other goods,
            services and events which we offer that are similar to those that
            you have already purchased or enquired about unless You have opted
            not to receive such information.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>To manage Your requests: </span>
            To attend and manage Your requests to Us.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>For business transfers: </span>
            We may use Your information to evaluate or conduct a merger,
            divestiture, restructuring, reorganization, dissolution, or other
            sale or transfer of some or all of Our assets, whether as a going
            concern or as part of bankruptcy, liquidation, or similar
            proceeding, in which Personal Data held by Us about our Service
            users is among the assets transferred.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>For other purposes: </span>
            We may use Your information for other purposes, such as data
            analysis, identifying usage trends, determining the effectiveness of
            our promotional campaigns and to evaluate and improve our Service,
            products, services, marketing and your experience.
          </Trans>
        </p>
        <p>
          <Trans>
            We may share Your personal information in the following situations:
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>With Service Providers:</span>
            We may share Your personal information with Service Providers to
            monitor and analyze the use of our Service, to contact You.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>For business transfers:</span>
            We may share or transfer Your personal information in connection
            with, or during negotiations of, any merger, sale of Company assets,
            financing, or acquisition of all or a portion of Our business to
            another company.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>With Affiliates: </span>
            We may share Your information with Our affiliates, in which case we
            will require those affiliates to honor this Privacy Policy.
            Affiliates include Our parent company and any other subsidiaries,
            joint venture partners or other companies that We control or that
            are under common control with Us.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>With business partners: </span>
            We may share Your information with Our business partners to offer
            You certain products, services or promotions.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>With other users:</span>
            when You share personal information or otherwise interact in the
            public areas with other users, such information may be viewed by all
            users and may be publicly distributed outside.
          </Trans>
        </p>
        <p>
          <Trans>
            <span className='font-bold'>With Your consent: </span>
            We may disclose Your personal information for any other purpose with
            Your consent.
          </Trans>
        </p>
        <h3>
          <Trans>Retention of Your Personal Data</Trans>
        </h3>
        <p>
          <Trans>
            The Company will retain Your Personal Data only for as long as is
            necessary for the purposes set out in this Privacy Policy. We will
            retain and use Your Personal Data to the extent necessary to comply
            with our legal obligations (for example, if we are required to
            retain your data to comply with applicable laws), resolve disputes,
            and enforce our legal agreements and policies.
          </Trans>
        </p>
        <p>
          <Trans>
            The Company will also retain Usage Data for internal analysis
            purposes. Usage Data is generally retained for a shorter period of
            time, except when this data is used to strengthen the security or to
            improve the functionality of Our Service, or We are legally
            obligated to retain this data for longer time periods.
          </Trans>
        </p>

        <h3>
          <Trans>Transfer of Your Personal Data</Trans>
        </h3>
        <p>
          <Trans>
            Your information, including Personal Data, is processed at the
            Company's operating offices and in any other places where the
            parties involved in the processing are located. It means that this
            information may be transferred to — and maintained on — computers
            located outside of Your state, province, country or other
            governmental jurisdiction where the data protection laws may differ
            than those from Your jurisdiction.
          </Trans>
        </p>
        <p>
          <Trans>
            Your consent to this Privacy Policy followed by Your submission of
            such information represents Your agreement to that transfer.
          </Trans>
        </p>
        <p>
          <Trans>
            The Company will take all steps reasonably necessary to ensure that
            Your data is treated securely and in accordance with this Privacy
            Policy and no transfer of Your Personal Data will take place to an
            organization or a country unless there are adequate controls in
            place including the security of Your data and other personal
            information.
          </Trans>
        </p>

        <h3>
          <Trans>Disclosure of Your Personal Data</Trans>
        </h3>
        <h4>
          <Trans>Business Transactions</Trans>
        </h4>
        <p>
          <Trans>
            If the Company is involved in a merger, acquisition or asset sale,
            Your Personal Data may be transferred. We will provide notice before
            Your Personal Data is transferred and becomes subject to a different
            Privacy Policy.
          </Trans>
        </p>
        <h4>
          <Trans>Law enforcement</Trans>
        </h4>
        <p>
          <Trans>
            Under certain circumstances, the Company may be required to disclose
            Your Personal Data if required to do so by law or in response to
            valid requests by public authorities (e.g. a court or a government
            agency).
          </Trans>
        </p>
        <h4>
          <Trans>Other legal requirements</Trans>
        </h4>
        <p>
          <Trans>
            The Company may disclose Your Personal Data in the good faith belief
            that such action is necessary to:
          </Trans>
        </p>
        <ul>
          <li>
            <Trans>Comply with a legal obligation</Trans>
          </li>
          <li>
            <Trans>
              Protect and defend the rights or property of the Company
            </Trans>
          </li>
          <li>
            <Trans>
              Prevent or investigate possible wrongdoing in connection with the
              Service
            </Trans>
          </li>
          <li>
            <Trans>
              Protect the personal safety of Users of the Service or the public
            </Trans>
          </li>
          <li>
            <Trans>Protect against legal liability</Trans>
          </li>
        </ul>
        <h3>
          <Trans>Security of Your Personal Data</Trans>
        </h3>
        <p>
          <Trans>
            The security of Your Personal Data is important to Us, but remember
            that no method of transmission over the Internet, or method of
            electronic storage is 100% secure. While We strive to use
            commercially acceptable means to protect Your Personal Data, We
            cannot guarantee its absolute security.
          </Trans>
        </p>
        <h2>
          <Trans>Changes to this Privacy Policy</Trans>
        </h2>
        <p>
          <Trans>
            We may update Our Privacy Policy from time to time. We will notify
            You of any changes by posting the new Privacy Policy on this page.
          </Trans>
        </p>
        <p>
          <Trans>
            We will let You know via email and/or a prominent notice on Our
            Service, prior to the change becoming effective and update the "Last
            updated" date at the top of this Privacy Policy.
          </Trans>
        </p>
        <p>
          <Trans>
            You are advised to review this Privacy Policy periodically for any
            changes. Changes to this Privacy Policy are effective when they are
            posted on this page.
          </Trans>
        </p>
        <h2>
          <Trans>Your Choices</Trans>
        </h2>
        <p>
          <Trans>
            <span className='font-bold'>Tad.ai</span> collects and uses personal
            information to provide and improve our services, personalize your
            experience, and deliver relevant advertising. We respect your
            privacy and have outlined various ways you can opt out of certain
            uses of your personal information. Please review the information
            below to exercise your rights:
          </Trans>
        </p>
        <ul>
          <li>
            <Trans>
              <span className='font-bold'>Browser Settings:</span> You can
              configure your browser settings to refuse the use of cookies.
              Please note that doing so may affect the full functionality of our
              website and the services we provide.
            </Trans>
          </li>
          <li>
            <Trans>
              <span className='font-bold'>
                Opt-Out for Personalized Advertising:
              </span>{' '}
              If you would like to opt out of personalized advertising, please
              contact us at <EmailLink />.
            </Trans>
          </li>
          <li>
            <Trans>
              <span className='font-bold'>Account Deactivation:</span> You can
              request to deactivate your account at any time by contacting us.
              Please email us at <EmailLink /> with your request. Once your
              account is deactivated, we will ensure that your personal
              information is handled in accordance with applicable privacy
              regulations.
            </Trans>
          </li>
        </ul>
        <p>
          <Trans>
            If you have any questions or concerns regarding our privacy
            practices, please do not hesitate to contact us through the above
            channels. We are committed to addressing your privacy-related
            inquiries promptly.
          </Trans>
        </p>
        <h2>
          <Trans>Contact Us</Trans>
        </h2>
        <p>
          <Trans>
            If you have any questions about this Privacy Policy, You can contact
            us via <EmailLink />.
          </Trans>
        </p>

        {/* --- */}
      </div>
    </div>
  )
}
const LayoutsPage = withLayouts(Page, [
  (props) => {
    const isApp = useIsAppQuery()
    return (
      <DefaultLayout
        {...props}
        minHScreen
        main={{
          className: 'bg-black',
        }}
        header={isApp ? null : undefined}
        footer={isApp ? null : undefined}
      />
    )
  },
  (props) => {
    const { t } = useLingui()
    return (
      <RootLayout
        {...props}
        seo={{
          title: t`Privacy Policy | Tad AI`,
          description: t`Understand how Tad AI safeguards your information with our comprehensive Privacy Policy. Explore our commitment to data protection, usage, and transparency for a secure user experience`,
        }}
      />
    )
  },
  (props) => {
    return <SiteSpecificContextLayout {...props} />
  },
  AntdLayout,
])

export default LayoutsPage
export const getServerSideProps = getServerSideData({
  cacheControl: true,
})(async (_, ctx, helpers) => {
  return {}
})
